import {useEffect, useRef} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {PATHS} from 'routes/constants';
import {LAYOUT_MODE_KEY} from 'utils/constants';
import {SHORT_LINK_TYPE} from '@chekin/core';

const getHousingCheckinOnlineInitPatch = (token = '', layout: string | null) => {
  const path =
    layout === 'kiosko'
      ? `${token}/${PATHS.searchReservation.welcomeScreen}`
      : `${token}/${PATHS.searchReservation.main}`;
  return path;
};

const getDirectReservationPath = (token = '', search = '') => {
  return `${token}/${PATHS.searchReservation.directReservation}${search}`;
};

const getUrlPathsFromShortLink = (
  type = '',
  token = '',
  layout: string | null,
  search = '',
) => {
  return {
    [SHORT_LINK_TYPE.main]: '',
    [SHORT_LINK_TYPE.payment]: `${token}/${PATHS.payments.main}`,
    [SHORT_LINK_TYPE.upselling]: `${token}/${PATHS.recommendations.main}`,
    [SHORT_LINK_TYPE.lock]: `${token}/${PATHS.remoteAccess}`,
    [SHORT_LINK_TYPE.ivHousing]: `${token}/${PATHS.verifyOnHousing}`,
    [SHORT_LINK_TYPE.housingCheckinOnline]: getHousingCheckinOnlineInitPatch(
      token,
      layout,
    ),
    [SHORT_LINK_TYPE.housingGuidebook]: `${token}/${PATHS.housingGuidebooks}`,
    [SHORT_LINK_TYPE.housingDirectReservation]: getDirectReservationPath(token, search),
  }[type];
};

type UseRedirectToUrlFromShortLinkProps = {
  linkType?: string;
  token?: string;
};

function useRedirectToUrlFromShortLink({
  linkType,
  token,
}: UseRedirectToUrlFromShortLinkProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hasBeenRedirected = useRef(false);

  const queryParamLayout = searchParams.get('layout');
  const storedLayout = localStorage.getItem(LAYOUT_MODE_KEY);
  const layout = queryParamLayout ?? storedLayout ?? 'default';

  useEffect(
    function redirectToUrlFromShortLink() {
      if (hasBeenRedirected.current) return;

      const redirectUrl = getUrlPathsFromShortLink(
        linkType,
        token,
        layout,
        location.search,
      );
      if (redirectUrl) {
        navigate(redirectUrl, {replace: true});
        hasBeenRedirected.current = true;
      }
    },
    [token, linkType, navigate, layout, location.pathname, location.search],
  );
}

export {useRedirectToUrlFromShortLink};
